import React, { useState, useEffect, useRef } from 'react';
import Navbar from '../components/Navbar';
import { useNavigate } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import { useSelector } from "react-redux";
import NavbarMob from '../components/NavbarMob';
import Footer from '../components/Footer';
import FooterMob from '../components/FooterMob';
import banner1 from '../images/Home/banner2.webp';
import bannermob from '../images/Home/bannermob2.webp';
import line from '../images/Home/line.png';
import product1 from '../images/Home/product2.webp';
import heart from '../images/Home/heart.png';
import filledHeart from '../images/Home/heart2.png';
import men from '../images/Home/MEN2.png';
import women from '../images/Home/WOMEN2.png';
import button from '../images/Home/button.png';
import combined from '../images/Home/groupFootr.jpeg';
import star from '../images/Home/Star.png';
import starmob from '../images/Home/startmob.png';
import axios from 'axios';
import { useStore } from '../useStore';

function Home() {
    const categoryImages = useSelector((state) => state.category.images);
    const isMobile = useMediaQuery({ query: '(max-width: 768px)' });
    const isTab = useMediaQuery({ query: '(max-width: 1024px)' });

    const [categories, setCategories] = useState([]);
    const [newArrivals, setNewArrivals] = useState([]);
    const [allProducts, setAllProducts] = useState([]);
    const [startIndex, setStartIndex] = useState(0);
    const [disableNext, setDisableNext] = useState(false);
    const [disablePrev, setDisablePrev] = useState(true);
    const [loginId, setLoginId] = useState(null);
    const [wishlistItems, setWishlistItems] = useState([]);
    const [visibleProducts, setVisibleProducts] = useState([]);
    const [loadingProducts, setLoadingProducts] = useState(true);
    const [targetProductCount] = useState(100);

    const shopByCategoryRef = useRef(null);
    const mountedRef = useRef(true);
    const imageCacheRef = useRef(new Map()); // Cache for image existence checks

    const { showDropdown, setShowDropdown } = useStore();
    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
    const apiLocalUrl = process.env.REACT_APP_API_LOCAL_URL;
    const userId = useSelector((state) => state.user.id);
    const navigate = useNavigate();

    // mountedRef for cleanup
    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    // Get loginId from localStorage
    useEffect(() => {
        const storedLoginId = localStorage.getItem('loginId');
        setLoginId(storedLoginId);
    }, []);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // Handle shop now click
    const handleShopNowClick = () => {
        searchpage();
    };

    // Fetch categories
    const allowedCategoryIds = [4, 5, 6, 7, 219, 301, 358, 359, 218, 450, 448, 616];
    useEffect(() => {
        fetch(`${apiBaseUrl}/getCategories/BLACKBATON_2526`)
            .then(res => res.json())
            .then(data => {
                const filtered = data.filter(cat => allowedCategoryIds.includes(cat.Id));
                if (mountedRef.current) {
                    setCategories(filtered);
                    setDisableNext(filtered.length <= 3);
                }
            })
            .catch(console.error);
    }, [apiBaseUrl]);

    // Fetch New Arrivals
    useEffect(() => {
        const fetchNewArrivals = async () => {
            try {
                const res = await fetch(`${apiBaseUrl}/getNewArrival_BestSeller_LimitedSale/BLACKBATON_2526?Id=NewArrivals`);
                const data = await res.json();
                if (mountedRef.current) setNewArrivals(data.slice(0, 8));
            } catch (err) {
                console.error(err);
            }
        };
        fetchNewArrivals();
    }, [apiBaseUrl]);

    // Optimized: Check multiple images at once
    const checkImagesBatch = async (imageCodes) => {
        const batchSize = 10; // Check 10 images at once
        const results = {};
        
        for (let i = 0; i < imageCodes.length; i += batchSize) {
            const batch = imageCodes.slice(i, i + batchSize);
            const batchPromises = batch.map(async (code) => {
                // Check cache first
                if (imageCacheRef.current.has(code)) {
                    return { code, exists: imageCacheRef.current.get(code) };
                }
                
                try {
                    const res = await fetch(`${apiLocalUrl}/check-image/${code}`);
                    const data = await res.json();
                    imageCacheRef.current.set(code, data.exists); // Cache result
                    return { code, exists: data.exists };
                } catch (err) {
                    console.error("Image check failed:", err);
                    imageCacheRef.current.set(code, false); // Cache failure
                    return { code, exists: false };
                }
            });
            
            const batchResults = await Promise.all(batchPromises);
            batchResults.forEach(result => {
                results[result.code] = result.exists;
            });
            
            // Small delay between batches to avoid overwhelming the server
            if (i + batchSize < imageCodes.length) {
                await new Promise(resolve => setTimeout(resolve, 50));
            }
        }
        
        return results;
    };

    // Optimized: Get available color for a product
    const getAvailableColorForProduct = async (productId, productData) => {
        try {
            const response = await axios.get(`${apiBaseUrl}/getProductDetails/BLACKBATON_2526?Id=${productId}`);
            if (response.status === 200) {
                const data = response.data;
                const distinctColorsArr = [...new Set(
                    data.filter(item => item.ItemId === productId).map(item => item.Color?.trim()).filter(Boolean)
                )];

                // Check all colors at once
                const colorCodes = distinctColorsArr.map(color => `${productId}_${color}.webp`);
                const colorResults = await checkImagesBatch(colorCodes);
                
                // Find first available color
                for (const color of distinctColorsArr) {
                    const code = `${productId}_${color}.webp`;
                    if (colorResults[code]) {
                        return color;
                    }
                }
                return null;
            }
        } catch (error) {
            console.error('Error:', error);
            return null;
        }
    };

    // Optimized: Check if image exists for a product
    const checkImageExists = async (product) => {
        try {
            const imageCodesToCheck = [];
            
            // First check displayFirst image
            if (product.displayFirst) {
                const displayFirstCode = `${product.displayFirst}.webp`;
                imageCodesToCheck.push(displayFirstCode);
            }
            
            // Check images in parallel
            const imageResults = await checkImagesBatch(imageCodesToCheck);
            
            // If displayFirst exists, use it
            if (product.displayFirst) {
                const displayFirstCode = `${product.displayFirst}.webp`;
                if (imageResults[displayFirstCode]) {
                    return { exists: true, code: displayFirstCode, color: null };
                }
            }

            // If displayFirst doesn't exist, check with colors
            const color = await getAvailableColorForProduct(product.ID, product);
            if (color) {
                const colorCode = `${product.ID}_${color}.webp`;
                return { exists: true, code: colorCode, color };
            }

            return { exists: false, code: null, color: null };
        } catch (error) {
            console.error('Error checking image:', error);
            return { exists: false, code: null, color: null };
        }
    };

    // Optimized: Process products in batches
    const processProductsBatch = async (products, batchSize = 5) => {
        const validProducts = [];
        
        for (let i = 0; i < products.length && validProducts.length < targetProductCount; i += batchSize) {
            const batch = products.slice(i, i + batchSize);
            
            const batchPromises = batch.map(async (product) => {
                if (!product || validProducts.some(p => p.ID === product.ID)) {
                    return null;
                }
                
                try {
                    const imageCheck = await checkImageExists(product);
                    if (imageCheck.exists) {
                        return {
                            ...product,
                            displayFirst: imageCheck.code?.replace('.webp', '') || product.displayFirst
                        };
                    }
                } catch (err) {
                    console.error('Error checking product', product?.ID, err);
                }
                return null;
            });
            
            const batchResults = await Promise.all(batchPromises);
            const validBatchProducts = batchResults.filter(Boolean);
            
            validProducts.push(...validBatchProducts);
            
            // Update UI with new products immediately
            if (mountedRef.current && validBatchProducts.length > 0) {
                setAllProducts(prev => {
                    const newProducts = validBatchProducts.filter(newProd => 
                        !prev.some(p => p.ID === newProd.ID)
                    );
                    return [...prev, ...newProducts];
                });
                
                // Stagger visibility for animation
                setTimeout(() => {
                    if (!mountedRef.current) return;
                    setVisibleProducts(prev => {
                        const newIds = validBatchProducts
                            .map(p => p.ID)
                            .filter(id => !prev.includes(id));
                        return [...prev, ...newIds];
                    });
                }, 50);
            }
            
            // Small delay between batches
            if (i + batchSize < products.length && validProducts.length < targetProductCount) {
                await new Promise(resolve => setTimeout(resolve, 100));
            }
        }
        
        return validProducts;
    };

    // Optimized: Fetch products with batched processing
    useEffect(() => {
        const fetchProductsWithAvailability = async () => {
            if (!apiBaseUrl) return;
            setLoadingProducts(true);
            setAllProducts([]);
            setVisibleProducts([]);
            imageCacheRef.current.clear(); // Clear cache on new load

            try {
                const response = await fetch(`${apiBaseUrl}/getProductByFilter/BLACKBATON_2526?filter=`);
                const allData = await response.json();
                const initialProducts = Array.isArray(allData) ? allData : [];

                console.log(allData.length,'length home page');
                
                // Remove duplicates by ID
                const uniqueProducts = initialProducts.filter((product, index, self) =>
                    index === self.findIndex(p => p.ID === product.ID)
                );

                // Process products in batches
                await processProductsBatch(uniqueProducts, 8); // Increased batch size
                
            } catch (error) {
                console.error('Error fetching products:', error);
            } finally {
                if (mountedRef.current) setLoadingProducts(false);
            }
        };

        fetchProductsWithAvailability();
    }, [apiBaseUrl, targetProductCount, apiLocalUrl]);

    // Fetch wishlist items
    useEffect(() => {
        const fetchWishlistItems = async () => {
            try {
                const activeUserId = loginId || userId;
                if (!activeUserId) return;

                const response = await fetch(`${apiBaseUrl}/wishlist/items/BLACKBATON_2526/${activeUserId}`);
                const data = await response.json();

                if (data.success && mountedRef.current) {
                    setWishlistItems(data.items);
                }
            } catch (error) {
                console.error("Error fetching wishlist items:", error);
            }
        };
        fetchWishlistItems();
    }, [loginId, apiBaseUrl, userId]);

    // Wishlist handlers (unchanged)
    const addToWishlist = async (product) => {
        const apiUrl = `${apiBaseUrl}/wishlist/add/BLACKBATON_2526`;
        const activeUserId = loginId || userId;
        if (!activeUserId) {
            alert("Please Login First to Continue.");
            return;
        }

        const payload = {
            ID: product.ID,
            ItemName: product.ItemName,
            MRP: product.MRP,
            ledcode: activeUserId,
            Qty: product.Qty,
            UniqueCode: product.UniqueCode,
            DisplayFirst: product.displayFirst
        };

        try {
            const res = await fetch(apiUrl, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload)
            });

            if (res.ok) {
                setWishlistItems(prev => [...prev, product]);
                alert("Item added to wishlist!");
            } else {
                alert("Already in Wishlist");
            }
        } catch (error) {
            alert("An error occurred while adding to wishlist.");
        }
    };

    const removeFromWishlist = async (productId) => {
        try {
            const res = await fetch(`${apiBaseUrl}/wishlist/delete/BLACKBATON_2526/${productId}`, {
                method: 'DELETE',
            });
            if (res.ok) {
                setWishlistItems(prev => prev.filter(item => item.ID !== productId));
            }
        } catch (error) {
            console.error('Error removing from wishlist:', error);
        }
    };

    // Navigation handlers (unchanged)
    const fullimage = (id, itemName) => {
        navigate(`/fullimage?I=${btoa(String(id))}&N=${btoa(itemName)}`, { state: { id, itemName } });
    };

    const productPage = (categoryId, categoryName) => {
        navigate('/products', { state: { categoryId, categoryName } });
    };

    const newarrival = () => {
        navigate('/subcategoryproducts', { state: { categoryId: 163, categoryName: 'NEW ARRIVAL' } });
    };

    const searchpage = () => {
        navigate('/searchpage', { state: { filtered: '' } });
    };

    const mensubcategorypage = () => {
        navigate('/subcategoryproducts', { state: { categoryId: 627, categoryName: "MEN'S" } });
    };

    const womensubcategorypage = () => {
        navigate('/subcategoryproducts', { state: { categoryId: 628, categoryName: "WOMEN'S" } });
    };

    const accessoriessubcategorypage = () => {
        navigate('/subcategoryproducts', { state: { categoryId: 301, categoryName: "ACCESSORIES" } });
    };

    const kidssubcategorypage = () => {
        navigate('/subcategoryproducts', { state: { categoryId: 629, categoryName: "KID'S" } });
    };

    // Category slider functions (unchanged)
    const nextSlide = () => {
        if (startIndex < categories.length - 3) {
            setStartIndex(prevIndex => prevIndex + 1);
            setDisablePrev(false);
            if (startIndex + 1 >= categories.length - 3) {
                setDisableNext(true);
            }
        }
    };

    const prevSlide = () => {
        if (startIndex > 0) {
            setStartIndex(prevIndex => prevIndex - 1);
            setDisableNext(false);
            if (startIndex - 1 === 0) {
                setDisablePrev(true);
            }
        }
    };

    const isProductVisible = (productId) => {
        return visibleProducts.includes(productId);
    };

    // Get image URL with fallback (unchanged)
    const getProductImageUrl = (product) => {
        if (product.displayFirst) {
            return `${apiLocalUrl}/uploads/${product.displayFirst}.webp?v=${Date.now()}`;
        }
        return product1; // Fallback image
    };

    // Rest of your JSX remains exactly the same...
    return (
        <div className='min-h-screen flex flex-col' onClick={() => setShowDropdown(false)}>
            {isMobile ? <NavbarMob /> : <Navbar />}

            {/* Banner Section */}
            <div className='w-full lg:h-[735px] md:h-[600px] h-[565px] relative lg:mt-[175px] md:mt-[175px] mt-[120px]'>
                <img src={banner1} alt="banner1" className='w-full h-full md:block hidden' />
                <img src={bannermob} alt="bannermob" className='w-full h-full md:hidden' />
                <div className="absolute top-0 left-0 w-full h-full flex flex-col gap-3 items-center justify-center">
                    <span className="text-white lg:text-6xl text-3xl font-[800] font-montserrat text-center">
                        ELEVATE YOUR<br /> GAME, STYLE ON!
                    </span>
                    <span className="text-white text-xl font-[500] font-montserrat lg:block hidden text-center">
                        Discover cutting-edge apparel and accessories for every sport,
                        crafted for<br /> comfort, style, and unstoppable performance
                    </span>
                    <span className="text-white text-sm font-[500] font-montserrat lg:hidden text-center">
                        Discover cutting-edge apparel and accessories for<br /> every sport,
                        crafted for comfort, style,<br /> and unstoppable performance
                    </span>
                </div>
            </div>

            {/* Main Content */}
            <div className='w-full h-auto lg:px-12 px-3 lg:gap-12 gap-6 lg:pt-12 pt-6 flex flex-col' ref={shopByCategoryRef}>

                {/* Shop By Category */}
                <div className='flex flex-col gap-2 mt-3'>
                    <div className='flex flex-row justify-between w-full'>
                        <div className='flex flex-row gap-4 items-center'>
                            <img src={line} alt="line" />
                            <span className='lg:text-xl text-base font-[600] font-montserrat'>Shop By Category</span>
                        </div>
                        <div className='flex flex-row gap-2 items-center'>
                            <div
                                className={`w-[41px] h-[41px] bg-[#F5F5F5] rounded-full flex justify-center items-center font-bold cursor-pointer ${disablePrev ? 'opacity-50 cursor-not-allowed' : ''}`}
                                onClick={prevSlide}
                            >
                                {"<"}
                            </div>
                            <div
                                className={`w-[41px] h-[41px] bg-[#F5F5F5] rounded-full flex justify-center items-center font-bold cursor-pointer ${disableNext ? 'opacity-50 cursor-not-allowed' : ''}`}
                                onClick={nextSlide}
                            >
                                {">"}
                            </div>
                        </div>
                    </div>

                    {/* Desktop Categories */}
                    <div className='grid-cols-3 w-full gap-2 md:grid hidden'>
                        {categories.slice(startIndex, startIndex + 3).map((category) => (
                            <div key={category.Id} className="lg:h-[280px] h-[250px] relative">
                                <img
                                    src={categoryImages[category.Id]}
                                    alt={category.Name}
                                    className="w-full h-full object-cover"
                                />
                                <div className="absolute top-0 left-0 w-full h-full px-12 py-12 flex items-end">
                                    <div
                                        className="w-auto h-[35px] bg-[white] hover:bg-[red] hover:text-[white] rounded-2xl px-5 flex justify-center items-center text-base font-[600] font-montserrat cursor-pointer transition-colors duration-300"
                                        onClick={() => productPage(category.Id, category.Name)}
                                    >
                                        {category.Name}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Mobile Categories */}
                    <div className='grid grid-cols-1 w-full gap-2 md:hidden'>
                        {categories.slice(startIndex, startIndex + 1).map((category) => (
                            <div key={category.Id} className="lg:h-[280px] h-[250px] relative">
                                <img
                                    src={categoryImages[category.Id]}
                                    alt={category.Name}
                                    className="w-full h-full object-cover"
                                />
                                <div className="absolute top-0 left-0 w-full h-full px-12 py-12 flex items-end">
                                    <div
                                        className="w-auto h-[35px] bg-[white] hover:bg-[red] hover:text-[white] rounded-2xl px-5 flex justify-center items-center text-base font-[600] font-montserrat cursor-pointer transition-colors duration-300"
                                        onClick={() => productPage(category.Id, category.Name)}
                                    >
                                        {category.Name}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* New Arrivals */}
                {newArrivals.length > 0 && (
                    <div className='flex flex-col gap-6 mt-3'>
                        <div className='flex flex-row w-full'>
                            <div className='flex flex-row gap-4 items-center'>
                                <img src={line} alt="line" />
                                <span className='lg:text-xl text-base font-[600] font-montserrat'>New Arrivals</span>
                            </div>
                        </div>
                        <div className='grid lg:grid-cols-4 md:grid-cols-3 grid-cols-2 w-full gap-5'>
                            {newArrivals.map((product) => (
                                <div key={product.ID} className='flex flex-col gap-2 cursor-pointer' onClick={() => fullimage(product.ID, product.ItemName)}>
                                    <div className='lg:h-[382px] md:h-[300px] h-[200px] rounded-[12px] bg-[#EEEEEE] flex items-center justify-center relative'>
                                        <img
                                            src={`${apiLocalUrl}/uploads/${product.displayFirst}.webp?v=${Date.now()}`}
                                            alt={product.ItemName}
                                            onError={(e) => { e.target.onerror = null; e.target.src = product1; }}
                                            className='mix-blend-multiply w-full h-full object-cover rounded-[12px]'
                                        />
                                        <div className='absolute top-0 left-0 w-full h-full lg:p-6 p-2 flex justify-end'>
                                            <div
                                                className='lg:w-[33px] w-[23px] lg:h-[33px] h-[23px] rounded-full bg-white flex justify-center items-center cursor-pointer shadow-md'
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (wishlistItems.some(item => item.ID === product.ID)) {
                                                        removeFromWishlist(product.ID);
                                                    } else {
                                                        addToWishlist(product);
                                                    }
                                                }}
                                            >
                                                <img
                                                    src={wishlistItems.some(item => item.ID === product.ID) ? filledHeart : heart}
                                                    alt="heart"
                                                    className='w-3/4 h-3/4'
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className='flex justify-between w-full'>
                                        <div className='flex flex-col gap-1 w-full'>
                                            <span className='lg:text-base text-xs font-[600] font-montserrat text-left'>{product.ItemName}</span>
                                        </div>
                                        <div className='flex flex-col gap-1 items-end'>
                                            <div className='lg:w-[84px] lg:h-[37px] w-[70px] h-[30px] text-red-600 rounded-[8px] bg-[#F6F6F6] flex justify-center items-center text-sm font-[700] font-montserrat'>
                                                ₹{product.MRP}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className='w-full flex items-end justify-end'>
                            <span
                                className='text-right underline cursor-pointer hover:text-red-600 text-base font-[500] font-montserrat transition-colors duration-300'
                                onClick={newarrival}
                            >
                                See All
                            </span>
                        </div>
                    </div>
                )}

                {/* Men's & Women's Sections */}
                <div className='w-full flex md:flex-row flex-col lg:gap-0 gap-3 lg:h-[500px] h-auto mt-3'>
                    <div className='lg:w-[50%] w-full lg:h-full md:h-[450px] h-[284px] relative'>
                        <img src={men} alt="men" className='w-full h-full object-cover' />
                        <div className='absolute top-0 left-0 w-full h-full flex flex-col lg:gap-3 gap-2 justify-center items-center'>
                            <div className='w-auto lg:px-3 px-2 h-[57px] bg-black flex justify-center items-center'>
                                <span className='lg:text-5xl text-2xl font-[800] font-montserrat text-white'>Men's</span>
                            </div>
                            <img
                                src={button}
                                alt="button"
                                className='cursor-pointer hover:scale-105 transition-transform duration-300'
                                onClick={mensubcategorypage}
                            />
                        </div>
                    </div>
                    <div className='lg:w-[50%] w-full lg:h-full md:h-[450px] h-[284px] relative'>
                        <img src={women} alt="women" className='w-full h-full object-cover' />
                        <div className='absolute top-0 left-0 w-full h-full flex flex-col lg:gap-3 gap-2 justify-center items-center'>
                            <div className='w-auto lg:px-3 px-2 h-[57px] bg-black flex justify-center items-center'>
                                <span className='lg:text-5xl text-2xl font-[800] font-montserrat text-white'>Women's</span>
                            </div>
                            <img
                                src={button}
                                alt="button"
                                className='cursor-pointer hover:scale-105 transition-transform duration-300'
                                onClick={womensubcategorypage}
                            />
                        </div>
                    </div>
                </div>

                {/* All Products */}
                <div className='flex flex-col gap-6 mt-3'>
                    <div className='flex flex-row w-full'>
                        <div className='flex flex-row gap-4 items-center'>
                            <img src={line} alt="line" />
                            <span className='lg:text-xl text-base font-[600] font-montserrat'>ALL PRODUCTS</span>
                            {/* {allProducts.length > 0 && (
                                <span className="text-sm text-gray-500 ml-2">
                                    ({allProducts.length} products loaded)
                                </span>
                            )} */}
                        </div>
                    </div>

                    {loadingProducts && allProducts.length === 0 ? (
                        <div className="flex justify-center items-center py-12">
                            <div className="w-12 h-12 border-4 border-gray-300 border-t-red-600 rounded-full animate-spin"></div>
                        </div>
                    ) : allProducts.length > 0 ? (
                        <>
                            <div className='grid lg:grid-cols-4 md:grid-cols-3 grid-cols-2 w-full gap-5'>
                                {allProducts.map((product, index) => {
                                    const isVisible = isProductVisible(product.ID);

                                    return (
                                        <div
                                            key={product.ID}
                                            className={`flex flex-col gap-2 cursor-pointer transition-all duration-500 ease-out transform ${
                                                isVisible
                                                    ? 'opacity-100 translate-y-0 scale-100'
                                                    : 'opacity-0 translate-y-4 scale-95'
                                            }`}
                                            onClick={() => fullimage(product.ID, product.ItemName)}
                                            style={{
                                                transitionDelay: isVisible ? `${index * 50}ms` : '0ms'
                                            }}
                                        >
                                            <div className='lg:h-[382px] md:h-[300px] h-[200px] rounded-[12px] bg-[#EEEEEE] flex items-center justify-center relative'>
                                                <img
                                                    src={getProductImageUrl(product)}
                                                    alt={product.ItemName}
                                                    onError={(e) => {
                                                        e.target.onerror = null;
                                                        e.target.src = product1;
                                                    }}
                                                    className='mix-blend-multiply w-full h-full rounded-[12px] object-cover transition-transform duration-500 hover:scale-105'
                                                />
                                                <div className='absolute top-0 left-0 w-full h-full lg:p-6 p-2 flex justify-end'>
                                                    <div
                                                        className='lg:w-[33px] w-[23px] lg:h-[33px] h-[23px] rounded-full bg-white flex justify-center items-center cursor-pointer shadow-md'
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            if (wishlistItems.some(item => item.ID === product.ID)) {
                                                                removeFromWishlist(product.ID);
                                                            } else {
                                                                addToWishlist(product);
                                                            }
                                                        }}
                                                    >
                                                        <img
                                                            src={wishlistItems.some(item => item.ID === product.ID) ? filledHeart : heart}
                                                            alt="heart"
                                                            className='w-3/4 h-3/4'
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className='flex justify-between w-full'>
                                                <div className='flex flex-col gap-1 w-full'>
                                                    <span className='lg:text-base text-xs font-[600] font-montserrat text-left'>{product.ItemName}</span>
                                                </div>
                                                <div className='flex flex-col gap-1 items-end'>
                                                    <div className='lg:w-[84px] lg:h-[37px] w-[70px] h-[30px] text-red-600 rounded-[8px] bg-[#F6F6F6] flex justify-center items-center text-sm font-[700] font-montserrat'>
                                                        ₹{product.MRP}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            <div className='w-full flex items-end justify-end'>
                                <span
                                    className='text-right underline cursor-pointer hover:text-red-600 text-base font-[500] font-montserrat transition-colors duration-300'
                                    onClick={searchpage}
                                >
                                    See All
                                </span>
                            </div>
                        </>
                    ) : (
                        <div className="flex justify-center items-center py-12">
                            <span className="text-gray-500 text-lg">No products available at the moment.</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Footer Sections */}
            <div className="w-full h-auto lg:mt-12 mt-6 flex flex-col pb-12 gap-12">
                <div className="lg:h-[143px] h-[72px] bg-[#1A0136] w-full overflow-hidden relative flex items-center">
                    <div className="flex gap-6 animate-marquee whitespace-nowrap items-center">
                        {Array.from({ length: 15 }).map((_, index) => (
                            <React.Fragment key={index}>
                                <img src={star} alt="star" className="md:inline-block hidden" />
                                <img src={starmob} alt="starmob" className='inline-block md:hidden' />
                                <span className="text-white lg:text-2xl md:text-xl text-sm font-[600] font-montserrat inline-block">
                                    Find your perfect fit for every activity
                                </span>
                                <img src={starmob} alt="starmob" className='inline-block md:hidden' />
                            </React.Fragment>
                        ))}
                    </div>
                </div>

                <div className='flex lg:flex-row flex-col justify-start items-center bg-[#F6F6F6] w-full h-auto lg:gap-0 gap-3 pb-6'>
                    <div className='lg:w-[50%] w-full lg:px-[10%] px-3 flex flex-col gap-3 justify-center text-left lg:py-0 py-5'>
                        <span className='lg:text-3xl text-2xl font-[600] font-montserrat'>WE ELEVATE YOUR<br /> PERFORMANCE IN STYLE!</span>
                        <span className='lg:text-lg text-xs font-[400] font-montserrat'>"Combining cutting-edge design with ultimate<br /> comfort,
                            our sportswear keeps you moving,<br /> looking, and feeling your best—every step of the<br /> way."</span>
                        <span
                            className='lg:w-[168px] w-[93px] lg:h-[46px] h-[30px] bg-black flex justify-center items-center text-white rounded-[8px] font-[600] lg:text-lg text-xs font-montserrat cursor-pointer hover:bg-red-600 transition-colors duration-300'
                            onClick={handleShopNowClick}
                        >
                            Shop Now
                        </span>
                    </div>
                    <div className='lg:w-max w-full h-full lg:px-0 px-3'>
                        <img src={combined} alt="combined" className='w-full h-auto' />
                    </div>
                </div>
            </div>

            {isTab ? <FooterMob /> : <Footer />}
        </div>
    );
}

export default Home;